import React, { useState } from "react";
import {
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Container,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  FormControlLabel,
  Checkbox,
  Paper,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from "@mui/material";

// Import icons for enhanced UI
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { useMsal } from '@azure/msal-react';
import { Client } from "@microsoft/microsoft-graph-client";
import { Application } from "@microsoft/microsoft-graph-types";

interface DeleteAppFormProps {
  onCancel: () => void;
  onSuccess?: () => void; // Optional callback for cache refresh after deletion
}

const DeleteAppForm: React.FC<DeleteAppFormProps> = ({ onCancel, onSuccess }) => {
  const { instance, accounts } = useMsal();
  const [clientId, setClientId] = useState("");
  const [appData, setAppData] = useState<Application | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [safetyChecks, setSafetyChecks] = useState({
    understandConsequences: false,
    confirmedNotProduction: false,
    backupCreated: false
  });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState<{ success: boolean; message: string } | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);

  // Reset form when client ID changes
  const handleClientIdChange = (value: string) => {
    setClientId(value);
    setAppData(null);
    setConfirmationText("");
    setSafetyChecks({
      understandConsequences: false,
      confirmedNotProduction: false,
      backupCreated: false
    });
    setDeleteResult(null);
    setLookupError(null);
  };

  // Look up application by Client ID
  const handleLookupApp = async () => {
    if (!clientId.trim()) {
      setLookupError("Please enter a Client ID");
      return;
    }

    setLoading(true);
    setLookupError(null);
    setAppData(null);

    try {
      // Acquire token for Microsoft Graph API
      const response = await instance.acquireTokenSilent({
        scopes: ["Application.ReadWrite.All"],
        account: accounts[0]
      });

      // Initialize Graph client
      const client = Client.init({
        authProvider: (done) => done(null, response.accessToken)
      });

      // Look up application by appId (Client ID)
      const result = await client
        .api(`/applications?$filter=appId eq '${clientId.trim()}'`)
        .select("id,appId,displayName,spa,web,api,appRoles")
        .get();

      if (result.value && result.value.length > 0) {
        setAppData(result.value[0]);
      } else {
        setLookupError("No application found with this Client ID. Please verify the ID is correct.");
      }
    } catch (error: any) {
      console.error("Error looking up application:", error);
      setLookupError(`Failed to lookup application: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSafetyCheckChange = (checkName: keyof typeof safetyChecks) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setSafetyChecks(prev => ({
      ...prev,
      [checkName]: event.target.checked
    }));
  };

  const isDeleteEnabled = () => {
    if (!appData) return false;
    
    const expectedText = `DELETE ${appData.displayName}`;
    const allSafetyChecksComplete = Object.values(safetyChecks).every(check => check);
    const confirmationMatches = confirmationText === expectedText;
    
    return allSafetyChecksComplete && confirmationMatches;
  };

  const handleDeleteClick = () => {
    setShowConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!appData?.id) return;
    
    setShowConfirmDialog(false);
    setDeleting(true);
    
    try {
      // Acquire token for Microsoft Graph API
      const response = await instance.acquireTokenSilent({
        scopes: ["Application.ReadWrite.All"],
        account: accounts[0]
      });

      // Initialize Graph client
      const client = Client.init({
        authProvider: (done) => done(null, response.accessToken)
      });

      // Delete the application (this also deletes the service principal)
      await client.api(`/applications/${appData.id}`).delete();

      // ✅ TRIGGER: Cache refresh callback after successful deletion
      if (onSuccess) {
        onSuccess();
      }      
      
      setDeleteResult({
        success: true,
        message: `Application "${appData.displayName}" has been successfully deleted.`
      });
      
      // Reset form after successful deletion
      setTimeout(() => {
        setClientId("");
        setAppData(null);
      }, 3000);
      
    } catch (error: any) {
      console.error("Error deleting application:", error);
      setDeleteResult({
        success: false,
        message: `Failed to delete application: ${error.message || 'Unknown error'}`
      });
    } finally {
      setDeleting(false);
    }
  };

  const getAppTypeChips = (app: Application) => {
    const types = [];
    
    if (app.spa?.redirectUris?.length) {
      types.push({ label: 'SPA', color: '#2196f3' });
    }
    if (app.web?.redirectUris?.length) {
      types.push({ label: 'Web', color: '#4caf50' });
    }
    if (app.api?.oauth2PermissionScopes?.length) {
      types.push({ label: 'API', color: '#ff9800' });
    }
    if (app.appRoles?.length) {
      types.push({ label: 'Roles', color: '#9c27b0' });
    }
    
    return types;
  };

  const getRiskLevel = (app: Application) => {
    let riskScore = 0;
    let reasons = [];
    
    // Check for production indicators
    if (app.displayName?.toLowerCase().includes('prod')) {
      riskScore += 3;
      reasons.push('Contains "prod" in name');
    }
    
    // Check for API exposure
    if (app.api?.oauth2PermissionScopes?.length) {
      riskScore += 2;
      reasons.push('Exposes API scopes');
    }
    
    // Check for app roles
    if (app.appRoles?.length) {
      riskScore += 1;
      reasons.push('Has app roles defined');
    }
    
    // Check for multiple redirect URIs (suggests active use)
    const totalRedirects = (app.spa?.redirectUris?.length || 0) + (app.web?.redirectUris?.length || 0);
    if (totalRedirects > 2) {
      riskScore += 1;
      reasons.push('Multiple redirect URIs configured');
    }
    
    if (riskScore >= 4) return { level: 'HIGH', color: '#f44336', reasons };
    if (riskScore >= 2) return { level: 'MEDIUM', color: '#ff9800', reasons };
    return { level: 'LOW', color: '#4caf50', reasons };
  };

  // If deletion was successful, show success message
  if (deleteResult?.success) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
        <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 3,
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              borderBottom: '1px solid rgba(76, 175, 80, 0.3)'
            }}
          >
            <CheckCircleIcon sx={{ fontSize: '3rem', color: '#4caf50', mr: 2 }} />
            <Box>
              <Typography variant="h5" sx={{ color: '#2e7d32', fontWeight: 600, mb: 1 }}>
                Application Deleted Successfully
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {deleteResult.message}
              </Typography>
            </Box>
          </Box>
          
          <CardContent sx={{ p: 3 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={onCancel}
              sx={{ borderRadius: 2 }}
            >
              Back to Actions
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header Card */}
      <Card variant="outlined" sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            backgroundColor: '#fef2f2',
            borderBottom: '1px solid rgba(244, 67, 54, 0.2)'
          }}
        >
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />}
            onClick={onCancel}
            sx={{
              mr: 2,
              borderRadius: 2,
              pl: 1.5,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
                borderColor: "primary.main",
                color: "primary.main"
              }
            }}
          >
            Back
          </Button>
          
          <Box
            sx={{
              borderLeft: '4px solid #f44336',
              pl: 2,
              py: 0.5,
            }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#d32f2f',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
                letterSpacing: '-0.01em',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <DeleteForeverIcon sx={{ mr: 1, fontSize: '2rem' }} />
              Delete Application
            </Typography>
            
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontFamily: "'Segoe UI', Roboto, 'Open Sans', sans-serif",
              }}
            >
              Permanently remove an application registration from Azure AD
            </Typography>
          </Box>
        </Box>
      </Card>

      <Grid container spacing={3}>
        {/* Client ID Input */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: 'fit-content' }}>
            <CardContent sx={{ p: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  px: 3,
                  py: 2,
                  borderBottom: '1px solid rgba(0,0,0,0.12)',
                  backgroundColor: '#f9f9f9',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <SearchIcon sx={{ mr: 1, color: 'primary.main' }} />
                Enter Application Client ID
              </Typography>
              
              <Box sx={{ p: 3 }}>
                <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Additional Security Measure
                  </Typography>
                  <Typography variant="body2">
                    You must manually enter the Client ID of the application you want to delete. 
                    This prevents accidental selection and adds an extra layer of protection.
                  </Typography>
                </Alert>

                <TextField
                  label="Application Client ID"
                  value={clientId}
                  onChange={(e) => handleClientIdChange(e.target.value)}
                  placeholder="e.g., 12345678-1234-1234-1234-123456789012"
                  fullWidth
                  variant="outlined"
                  sx={{ 
                    mb: 2,
                    '& .MuiOutlinedInput-root': { borderRadius: 2 }
                  }}
                  helperText="Enter the exact Client ID (App ID) of the application you want to delete"
                />

                <Button
                  variant="contained"
                  onClick={handleLookupApp}
                  disabled={!clientId.trim() || loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  fullWidth
                  sx={{ 
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600
                  }}
                >
                  {loading ? 'Looking up Application...' : 'Lookup Application'}
                </Button>

                {/* Lookup Error */}
                {lookupError && (
                  <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                    {lookupError}
                  </Alert>
                )}

                {/* Application Found */}
                {appData && (
                  <Paper sx={{ p: 2, mt: 3, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'success.main' }}>
                      ✓ Application Found
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                      {appData.displayName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontFamily: 'monospace' }}>
                      {appData.appId}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {getAppTypeChips(appData).map((type, index) => (
                        <Chip
                          key={index}
                          label={type.label}
                          size="small"
                          sx={{
                            backgroundColor: `${type.color}20`,
                            color: type.color,
                            fontWeight: 500
                          }}
                        />
                      ))}
                    </Box>
                  </Paper>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Deletion Configuration */}
        <Grid item xs={12} md={6}>
          {appData ? (
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    px: 3,
                    py: 2,
                    borderBottom: '1px solid rgba(0,0,0,0.12)',
                    backgroundColor: '#fef2f2',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    color: '#d32f2f'
                  }}
                >
                  <WarningAmberIcon sx={{ mr: 1 }} />
                  Deletion Configuration
                </Typography>
                
                <Box sx={{ p: 3 }}>
                  {/* Risk Assessment */}
                  {(() => {
                    const risk = getRiskLevel(appData);
                    return (
                      <Alert 
                        severity={risk.level === 'HIGH' ? 'error' : risk.level === 'MEDIUM' ? 'warning' : 'info'}
                        sx={{ mb: 3, borderRadius: 2 }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Risk Level: {risk.level}
                        </Typography>
                        <List dense sx={{ mt: 1 }}>
                          {risk.reasons.map((reason, index) => (
                            <ListItem key={index} sx={{ px: 0, py: 0 }}>
                              <ListItemText primary={`• ${reason}`} />
                            </ListItem>
                          ))}
                        </List>
                      </Alert>
                    );
                  })()}

                  {/* Safety Checks */}
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#d32f2f' }}>
                    Safety Confirmation
                  </Typography>
                  
                  <List dense>
                    <ListItem sx={{ px: 0 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={safetyChecks.understandConsequences}
                            onChange={handleSafetyCheckChange('understandConsequences')}
                            color="error"
                          />
                        }
                        label="I understand this action cannot be undone"
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={safetyChecks.confirmedNotProduction}
                            onChange={handleSafetyCheckChange('confirmedNotProduction')}
                            color="error"
                          />
                        }
                        label="I confirm this is not a production application"
                      />
                    </ListItem>
                    <ListItem sx={{ px: 0 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={safetyChecks.backupCreated}
                            onChange={handleSafetyCheckChange('backupCreated')}
                            color="error"
                          />
                        }
                        label="I have created a backup if needed"
                      />
                    </ListItem>
                  </List>

                  <Divider sx={{ my: 3 }} />

                  {/* Confirmation Text */}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Type the application name to confirm deletion:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Please type <strong>DELETE {appData.displayName}</strong> to confirm
                  </Typography>
                  <TextField
                    value={confirmationText}
                    onChange={(e) => setConfirmationText(e.target.value)}
                    placeholder={`DELETE ${appData.displayName}`}
                    fullWidth
                    variant="outlined"
                    error={confirmationText.length > 0 && confirmationText !== `DELETE ${appData.displayName}`}
                    sx={{ 
                      mb: 3,
                      '& .MuiOutlinedInput-root': { borderRadius: 2 }
                    }}
                  />

                  {/* Delete Button */}
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteForeverIcon />}
                    onClick={handleDeleteClick}
                    disabled={!isDeleteEnabled() || deleting}
                    fullWidth
                    sx={{ 
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      backgroundColor: '#d32f2f',
                      '&:hover': {
                        backgroundColor: '#b71c1c'
                      }
                    }}
                  >
                    {deleting ? 'Deleting Application...' : 'Delete Application'}
                  </Button>

                  {/* Error Message */}
                  {deleteResult && !deleteResult.success && (
                    <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                      {deleteResult.message}
                    </Alert>
                  )}
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Card variant="outlined" sx={{ borderRadius: 2, minHeight: 400 }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Box textAlign="center">
                  <AppRegistrationIcon sx={{ fontSize: '4rem', color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Enter Client ID to Continue
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Enter the Client ID of the application you want to delete
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', pb: 1 }}>
          <ErrorOutlineIcon sx={{ color: '#f44336', mr: 1 }} />
          Final Confirmation
          <IconButton
            onClick={() => setShowConfirmDialog(false)}
            sx={{ ml: 'auto' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              This action is permanent and cannot be undone!
            </Typography>
          </Alert>
          <Typography>
            You are about to permanently delete the application:
          </Typography>
          <Paper sx={{ p: 2, mt: 2, backgroundColor: '#fef2f2' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {appData?.displayName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {appData?.appId}
            </Typography>
          </Paper>
          <Typography sx={{ mt: 2 }}>
            Are you absolutely sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setShowConfirmDialog(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteForeverIcon />}
            sx={{ borderRadius: 2 }}
          >
            Delete Application
          </Button>
        </DialogActions>
      </Dialog>

      {/* Info Card */}
      <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: '#f9f9f9', mt: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <InfoOutlinedIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">Important Information</Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(255, 193, 193, 0.3)', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#d32f2f' }}>
                  What gets deleted?
                </Typography>
                <Typography variant="body2">
                  The application registration, service principal, secrets, certificates, 
                  and all associated permissions will be permanently removed.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(255, 235, 156, 0.3)', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#f57c00' }}>
                  Impact on Applications
                </Typography>
                <Typography variant="body2">
                  Any applications or services using this registration will immediately 
                  lose the ability to authenticate and access resources.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, bgcolor: 'rgba(200, 230, 201, 0.3)', borderRadius: 1, height: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#2e7d32' }}>
                  Before Deleting
                </Typography>
                <Typography variant="body2">
                  Consider backing up the application configuration and ensure 
                  no dependent services are still using this registration.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default DeleteAppForm;